import { useState } from "react";
import { ShieldOff, RotateCcw, Lock, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../../ui/alert-dialog";
import { SectionHead, GoldButton, GhostButton, LevelPill, inputCls } from "./shared";

function RevokeDialog({ member, onClose, onConfirm }) {
  return (
    <AlertDialog open={!!member} onOpenChange={(o) => { if (!o) onClose(); }}>
      <AlertDialogContent className="bg-[#141414] border-[#333333] text-[#F5F5F5]" data-testid="pioneer-revoke-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif text-xl text-[#F5F5F5]">Revoke all access for {member?.name}?</AlertDialogTitle>
          <AlertDialogDescription className="text-[#D4D4D4]">
            Every module will be set to <span className="text-[#F5F5F5]">No Access</span> with immediate effect. Any active session is terminated and, on their next login, {member?.name?.split(" ")[0]} will see: <em className="text-[#C9A84C]">“Your access has been suspended. Please contact the Administrator.”</em> You can reinstate at any time.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="pioneer-revoke-cancel" className="bg-transparent border-[#333333] text-[#D4D4D4] hover:bg-[#242424] hover:text-[#F5F5F5]">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="pioneer-revoke-confirm" onClick={onConfirm} className="bg-[#C0392B] text-white hover:bg-[#A93226]">Revoke access</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function MemberRow({ m, roles, api, onChanged, onRevoke, onSelect, selected }) {
  const [busy, setBusy] = useState(false);
  const changeRole = async (role_key) => {
    setBusy(true);
    try { await api.patch(`/admin/pioneers/members/${m.id}`, { role_key }); toast.success(`${m.name} → ${roles.find((r) => r.key === role_key)?.label}`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change sub-role"); } finally { setBusy(false); }
  };
  const reinstate = async () => {
    setBusy(true);
    try { await api.post(`/admin/pioneers/members/${m.id}/reinstate`); toast.success(`${m.name} reinstated`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reinstate"); } finally { setBusy(false); }
  };
  const revoked = m.status === "revoked";
  const overrideCount = Object.keys(m.overrides || {}).length;

  return (
    <tr data-testid={`pioneer-member-row-${m.email}`} onClick={() => onSelect && onSelect(m)}
      className={`border-t border-[#262626] ${onSelect ? "cursor-pointer" : ""} ${selected ? "bg-[#C9A84C]/10" : "hover:bg-[#1A1A1A]"}`}>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-2">
          <span className="text-[#F5F5F5] font-medium" data-testid={`pioneer-member-name-${m.email}`}>{m.name}</span>
          {m.sovereign && <span title="Hardcoded full access — identical to Admin" data-testid={`pioneer-member-sovereign-${m.email}`} className="inline-flex items-center gap-1 text-[10px] font-grotesk uppercase tracking-[0.12em] text-[#C9A84C] border border-[#C9A84C]/50 px-1.5 py-0.5 rounded-sm"><Lock size={10} /> Full access</span>}
        </div>
        <div className="text-xs text-[#888888]">{m.title}</div>
        <div className="text-xs text-[#6B6B6B] font-mono">{m.email}</div>
      </td>
      <td className="py-3 pr-4" onClick={(e) => e.stopPropagation()}>
        <select data-testid={`pioneer-member-role-${m.email}`} value={m.role_key || ""} disabled={busy || revoked} onChange={(e) => changeRole(e.target.value)}
          className="bg-[#141414] border border-[#333333] text-[#F5F5F5] text-sm rounded-sm px-2 py-1.5 focus:border-[#C9A84C] outline-none disabled:opacity-40">
          {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        {m.sovereign && <div className="text-[10px] text-[#888888] mt-1">Sub-role is cosmetic; access is hardcoded to this profile.</div>}
      </td>
      <td className="py-3 pr-4">
        <span data-testid={`pioneer-member-status-${m.email}`} className={`text-[11px] font-grotesk uppercase tracking-[0.12em] px-2 py-1 rounded-sm border ${revoked ? "border-[#7A2E2E] text-[#E08A8A]" : "border-[#2F6B4F] text-[#7FD1A5]"}`}>{revoked ? "Revoked" : "Active"}</span>
        {overrideCount > 0 && <div className="text-[10px] text-[#C9A84C] mt-1">{overrideCount} override{overrideCount > 1 ? "s" : ""}</div>}
      </td>
      <td className="py-3 pr-4">
        <div className="flex flex-wrap gap-1 max-w-[260px]">
          {["vcb", "banking", "finance", "director_suite", "reports"].map((k) => (
            <span key={k} className="flex items-center gap-1 text-[10px] text-[#888888]"><LevelPill level={m.effective[k]} testid={`pioneer-member-eff-${m.email}-${k}`} />{k === "director_suite" ? "Dir." : k.toUpperCase().slice(0, 3)}</span>
          ))}
        </div>
      </td>
      <td className="py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        {revoked
          ? <GhostButton testid={`pioneer-member-reinstate-${m.email}`} onClick={reinstate} disabled={busy}><RotateCcw size={13} /> Reinstate</GhostButton>
          : <GhostButton testid={`pioneer-member-revoke-${m.email}`} onClick={() => onRevoke(m)} disabled={busy || m.sovereign} danger title={m.sovereign ? "Hardcoded full access cannot be revoked" : "Revoke all access"}><ShieldOff size={13} /> Revoke</GhostButton>}
      </td>
    </tr>
  );
}

function AddMember({ roles, api, onChanged }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", title: "", role_key: roles[0]?.key || "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/admin/pioneers/members", f); toast.success(`${f.name} added to The Pioneers`); setOpen(false); setF({ name: "", email: "", title: "", role_key: roles[0]?.key || "" }); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add member"); } finally { setBusy(false); }
  };
  if (!open) return <GoldButton testid="pioneer-member-add-toggle" onClick={() => setOpen(true)}><UserPlus size={14} /> Add Pioneer</GoldButton>;
  return (
    <div className="w-full grid md:grid-cols-[1fr_1fr_1fr_auto_auto] gap-3 border border-[#333333] rounded-sm p-4 bg-[#141414]" data-testid="pioneer-member-add-form">
      <input data-testid="pioneer-member-add-name" className={inputCls} placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <input data-testid="pioneer-member-add-email" className={inputCls} placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      <input data-testid="pioneer-member-add-title" className={inputCls} placeholder="Title (optional)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <select data-testid="pioneer-member-add-role" value={f.role_key} onChange={(e) => setF({ ...f, role_key: e.target.value })} className="bg-[#141414] border border-[#333333] text-[#F5F5F5] text-sm rounded-sm px-2 py-2 focus:border-[#C9A84C] outline-none">
        {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
      </select>
      <div className="flex gap-2"><GoldButton testid="pioneer-member-add-submit" onClick={submit} disabled={busy}>Add</GoldButton><GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton></div>
    </div>
  );
}

export function MembersPanel({ data, api, onChanged, onSelect, selectedId, compact }) {
  const [revoking, setRevoking] = useState(null);
  const confirmRevoke = async () => {
    const m = revoking; setRevoking(null);
    try { await api.post(`/admin/pioneers/members/${m.id}/revoke`); toast.success(`All access revoked for ${m.name}`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
  };
  return (
    <section className="admin-card" data-testid="pioneer-members-panel">
      <SectionHead kicker={compact ? "Members · revoke access" : "Category · Admin Dashboard only"} title={compact ? "Pioneer Members" : "The Pioneers"}
        action={!compact && <AddMember roles={data.roles} api={api} onChanged={onChanged} />} />
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#C9A84C]">
              <th className="pb-2 pr-4">Member</th><th className="pb-2 pr-4">Sub-role</th><th className="pb-2 pr-4">Status</th><th className="pb-2 pr-4">Effective access</th><th className="pb-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => <MemberRow key={m.id} m={m} roles={data.roles} api={api} onChanged={onChanged} onRevoke={setRevoking} onSelect={onSelect} selected={selectedId === m.id} />)}
          </tbody>
        </table>
      </div>
      <RevokeDialog member={revoking} onClose={() => setRevoking(null)} onConfirm={confirmRevoke} />
    </section>
  );
}
